/**
 * Auto-instrumentation ; installs passive listeners across the app.
 *
 * Captures:
 *   - clicks on any interactive element (buttons, links, [data-testid],
 *     [role="button"], inputs/labels)
 *   - outbound link clicks
 *   - rage clicks (3+ within 600ms on same element)
 *   - scroll-depth milestones (25/50/75/90/100%)
 *   - time-on-page milestones (15/30/60/180/300s)
 *   - section visibility (any [data-track-section])
 *   - form field focus/blur (field name only ; NO values, ever)
 *   - viewport resize and orientation changes (debounced)
 *   - errors (window.onerror + unhandledrejection)
 *
 * Everything funnels through `track(name, props)` in eventBus.js.
 */
import { track } from "./eventBus";

const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100];
const TIME_THRESHOLDS = [15, 30, 60, 180, 300];

let _installed = false;
let _cleanup = [];

function describeTarget(el) {
  if (!el) return null;
  const testid = el.getAttribute?.("data-testid");
  const trackName = el.getAttribute?.("data-track");
  const role = el.getAttribute?.("role");
  const text = (el.innerText || el.value || "").trim().slice(0, 64) || null;
  const tag = el.tagName?.toLowerCase();
  const href = el.getAttribute?.("href");
  const id = el.id || null;
  const cls = (typeof el.className === "string" ? el.className : "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 4)
    .join(" ");
  return { tag, testid, trackName, role, text, href, id, cls };
}

function findInteractiveAncestor(el) {
  let n = el;
  for (let i = 0; n && i < 6; i++) {
    if (!n.getAttribute) return null;
    const tag = n.tagName?.toLowerCase();
    if (
      tag === "a" ||
      tag === "button" ||
      tag === "label" ||
      tag === "input" ||
      tag === "select" ||
      tag === "textarea" ||
      n.getAttribute("role") === "button" ||
      n.getAttribute("data-testid") ||
      n.getAttribute("data-track")
    ) {
      return n;
    }
    n = n.parentElement;
  }
  return null;
}

// ---- Clicks (with rage-click detection) ------------------------------------
function installClickInstrumentation() {
  let lastClicks = []; // [{t, el}]
  const onClick = (e) => {
    const target = findInteractiveAncestor(e.target);
    if (!target) return;
    const meta = describeTarget(target);
    const now = performance.now();

    // Rage-click: 3+ in 600ms on same element
    lastClicks = lastClicks.filter((c) => now - c.t < 600);
    lastClicks.push({ t: now, el: target });
    const sameEl = lastClicks.filter((c) => c.el === target);
    if (sameEl.length >= 3) {
      track("rage_click", { ...meta, count: sameEl.length });
      lastClicks = [];
    }

    // Outbound vs internal
    let kind = "click";
    if (meta.tag === "a" && meta.href) {
      try {
        const u = new URL(meta.href, window.location.href);
        if (u.hostname && u.hostname !== window.location.hostname) {
          kind = "outbound_click";
        }
      } catch {
        /* ignore */
      }
    }
    // CTA shortcut ; anything with btn-gold / btn-ghost classes
    const cls = meta.cls || "";
    if (cls.includes("btn-gold") || cls.includes("btn-ghost")) {
      track("cta_click", meta);
      return;
    }
    track(kind, meta);
  };
  document.addEventListener("click", onClick, { capture: true, passive: true });
  _cleanup.push(() => document.removeEventListener("click", onClick, true));
}

// ---- Scroll depth ----------------------------------------------------------
function installScrollDepth() {
  const fired = new Set();
  const onScroll = () => {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop;
    const max = Math.max(
      doc.scrollHeight - window.innerHeight,
      1
    );
    const pct = Math.min(100, Math.round((scrollTop / max) * 100));
    for (const t of SCROLL_THRESHOLDS) {
      if (pct >= t && !fired.has(t)) {
        fired.add(t);
        track("scroll_depth", { pct: t });
      }
    }
  };
  let raf = null;
  const onScrollRaf = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = null;
      onScroll();
    });
  };
  window.addEventListener("scroll", onScrollRaf, { passive: true });
  _cleanup.push(() => window.removeEventListener("scroll", onScrollRaf));
  // Reset on route change is handled by useInstrumentation re-installing per route
}

// ---- Time on page ----------------------------------------------------------
function installTimeOnPage() {
  const fired = new Set();
  const start = Date.now();
  const tick = () => {
    const seconds = Math.floor((Date.now() - start) / 1000);
    for (const t of TIME_THRESHOLDS) {
      if (seconds >= t && !fired.has(t)) {
        fired.add(t);
        track("time_on_page", { seconds: t });
      }
    }
  };
  const interval = setInterval(tick, 5000);
  _cleanup.push(() => clearInterval(interval));
}

// ---- Section visibility (any [data-track-section]) -------------------------
// NOTE: section observation is owned by SiteShell on a per-route basis so
// route changes can rebind to fresh DOM. The global installer below is a no-op.
function installSectionVisibility() {
  /* intentionally empty ; see SiteShell.jsx */
}

// ---- Form field focus/blur (no values) -------------------------------------
function installFormInstrumentation() {
  const onFocus = (e) => {
    const el = e.target;
    if (!el || !el.tagName) return;
    const tag = el.tagName.toLowerCase();
    if (!["input", "textarea", "select"].includes(tag)) return;
    if (el.type === "hidden" || el.name === "company_website") return;
    track("form_field_focus", {
      field: el.name || el.id || el.getAttribute("data-testid"),
      kind: el.type || tag,
    });
  };
  const onBlur = (e) => {
    const el = e.target;
    if (!el || !el.tagName) return;
    const tag = el.tagName.toLowerCase();
    if (!["input", "textarea", "select"].includes(tag)) return;
    if (el.type === "hidden" || el.name === "company_website") return;
    track("form_field_blur", {
      field: el.name || el.id || el.getAttribute("data-testid"),
      kind: el.type || tag,
      hadValue: !!el.value,
    });
  };
  document.addEventListener("focusin", onFocus, { passive: true });
  document.addEventListener("focusout", onBlur, { passive: true });
  _cleanup.push(() => document.removeEventListener("focusin", onFocus));
  _cleanup.push(() => document.removeEventListener("focusout", onBlur));
}

// ---- Viewport changes ------------------------------------------------------
function installViewportInstrumentation() {
  let last = `${window.innerWidth}x${window.innerHeight}`;
  let timer = null;
  const onResize = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const next = `${window.innerWidth}x${window.innerHeight}`;
      if (next !== last) {
        track("viewport_change", { from: last, to: next });
        last = next;
      }
    }, 600);
  };
  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("orientationchange", onResize, { passive: true });
  _cleanup.push(() => window.removeEventListener("resize", onResize));
  _cleanup.push(() => window.removeEventListener("orientationchange", onResize));
}

// ---- Errors ----------------------------------------------------------------
function installErrorInstrumentation() {
  const onError = (e) =>
    track("js_error", {
      message: (e.message || "").slice(0, 240),
      source: (e.filename || "").slice(0, 240),
      lineno: e.lineno,
      colno: e.colno,
    });
  const onRejection = (e) =>
    track("promise_rejection", {
      reason: String(e.reason || "").slice(0, 240),
    });
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  _cleanup.push(() => window.removeEventListener("error", onError));
  _cleanup.push(() => window.removeEventListener("unhandledrejection", onRejection));
}

export function installAutoInstrumentation() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  installClickInstrumentation();
  installScrollDepth();
  installTimeOnPage();
  installSectionVisibility();
  installFormInstrumentation();
  installViewportInstrumentation();
  installErrorInstrumentation();
}

export function teardownAutoInstrumentation() {
  _cleanup.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
  _cleanup = [];
  _installed = false;
}
